import { getPurchasesInstance } from '../config/revenuecat';
import type { CustomerInfo, Package, Offerings } from '@revenuecat/purchases-js';

/**
 * Check if user has an active subscription
 */
export const hasActiveSubscription = async (): Promise<boolean> => {
  try {
    const purchases = getPurchasesInstance();
    if (!purchases) {
      console.error('RevenueCat not initialized - please ensure user is logged in');
      return false;
    }

    const customerInfo: CustomerInfo = await purchases.getCustomerInfo();
    console.log('Customer info retrieved:', customerInfo);
    
    // Check if user has any active entitlements
    const activeEntitlements = customerInfo.entitlements.active;
    return Object.keys(activeEntitlements).length > 0;
  } catch (error) {
    console.error('Failed to check subscription status:', error);
    return false;
  }
};

/**
 * Get current customer info
 */
export const getCustomerInfo = async (): Promise<CustomerInfo | null> => {
  try {
    const purchases = getPurchasesInstance();
    if (!purchases) {
      console.error('RevenueCat not initialized');
      return null;
    }

    return await purchases.getCustomerInfo();
  } catch (error) {
    console.error('Failed to get customer info:', error);
    return null;
  }
};

/**
 * Get available offerings and packages
 */
export const getOfferings = async (): Promise<Offerings | null> => {
  try {
    const purchases = getPurchasesInstance();
    if (!purchases) {
      console.error('RevenueCat not initialized - please ensure user is logged in');
      throw new Error('RevenueCat not initialized. Please log in first.');
    }

    const offerings = await purchases.getOfferings();
    console.log('Offerings retrieved:', offerings);
    return offerings;
  } catch (error) {
    console.error('Failed to get offerings:', error);
    throw error;
  }
};

/**
 * Purchase a package
 * The purchase will be associated with the user ID that was used to initialize RevenueCat
 */
export const purchasePackage = async (pkg: Package): Promise<CustomerInfo | null> => {
  try {
    const purchases = getPurchasesInstance();
    if (!purchases) {
      console.error('RevenueCat not initialized');
      return null;
    }

    console.log('=== Starting Purchase ===');
    console.log('Package:', pkg.identifier);
    console.log('Product:', pkg.webBillingProduct.identifier);
    console.log('App User ID:', purchases.getAppUserId());
    console.log('This purchase will be linked to App User ID:', purchases.getAppUserId());

    const result = await purchases.purchase({ rcPackage: pkg });
    
    console.log('✓ Purchase successful');
    console.log('Customer ID:', result.customerInfo.originalAppUserId);
    console.log('Active entitlements:', Object.keys(result.customerInfo.entitlements.active));
    
    return result.customerInfo;
  } catch (error) {
    console.error('Failed to purchase package:', error);
    throw error;
  }
};

/**
 * Get the management URL for the user to manage their subscription
 * Note: Management URLs may not be available immediately after purchase.
 * They require the billing provider to provision the management portal.
 */
export const getManagementUrl = async (): Promise<string | null> => {
  try {
    const purchases = getPurchasesInstance();
    if (!purchases) {
      console.error('RevenueCat not initialized');
      return null;
    }

    console.log('=== Fetching Management URL ===');
    
    // Force a fresh fetch by calling getCustomerInfo
    // This ensures we have the most up-to-date information from RevenueCat servers
    const customerInfo = await purchases.getCustomerInfo();
    
    console.log('Customer Info:', {
      originalAppUserId: customerInfo.originalAppUserId,
      activeSubscriptions: Array.from(customerInfo.activeSubscriptions),
      managementURL: customerInfo.managementURL,
      hasActiveEntitlements: Object.keys(customerInfo.entitlements.active).length > 0,
      entitlementKeys: Object.keys(customerInfo.entitlements.active),
    });
    
    // Get the management URL from customer info
    // This will redirect to the appropriate billing platform (Stripe, RC Billing, etc.)
    if (customerInfo.managementURL) {
      console.log('✓ Management URL found:', customerInfo.managementURL);
      return customerInfo.managementURL;
    } else {
      console.warn('⚠ No managementURL in customerInfo');
      console.warn('Possible reasons:');
      console.warn('  - The subscription was just created and URL is still being provisioned');
      console.warn('  - Using a test API key (test_ prefix) - try using sandbox key (rcb_sb_)');
      console.warn('  - The billing provider does not support management URLs');
      console.warn('  - Web Billing not properly configured in RevenueCat dashboard');
      
      // Check if this is a test key issue
      const apiKey = process.env.REACT_APP_REVENUECAT_API_KEY || '';
      if (apiKey.startsWith('test_')) {
        console.error('❌ You are using a TEST API key!');
        console.error('Test keys may not provide management URLs.');
        console.error('Please use your sandbox key (rcb_sb_...) or production key instead.');
      }
      
      return null;
    }
  } catch (error) {
    console.error('Failed to get management URL:', error);
    return null;
  }
};

/**
 * Transfer anonymous purchases to a logged-in user by switching the RC user in-place.
 * This avoids the page-reload loop that occurred when RC's browser-cached anonymous
 * session survived reloads.
 */
export const transferPurchasesToUser = async (userId: string, email?: string): Promise<void> => {
  console.log('=== Transfer: closing anonymous RC session and re-configuring with real user ID ===');
  console.log('Target User ID:', userId);

  // Re-initialize with the real userId so it becomes the originalAppUserId,
  // not just an alias on the pre-existing anonymous customer.
  const { initializeRevenueCat } = await import('../config/revenuecat');
  initializeRevenueCat(userId, email);
  console.log('✓ RevenueCat re-configured with real user ID:', userId);
};

export const subscriptionApi = {
  hasActiveSubscription,
  getCustomerInfo,
  getOfferings,
  purchasePackage,
  getManagementUrl,
  transferPurchasesToUser,
};
